// UploadSourcePicker — body для bottom-sheet выбора источника файла анализа.
// Содержит: title + subtitle + 3 кнопки источника + tip про PDF + Отмена.
// Также one-time disclaimer Dialog (Q2 — Sprint 4).
//
// Используется в:
//   - HomeEmpty (inline через BottomSheet)
//   - /upload route (UploadSheet — fallback deep-link, wrapper над BottomSheet)
//
// onClose() — закрыть лист (cancel / backdrop click / Esc).
// onUploaded(uploadId) — после успешного storeUpload → navigate to /processing/:id.

import { useEffect, useRef, useState } from 'react';
import { Camera, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios, { type AxiosError } from 'axios';
import { api } from '@/api/client';
import { useStoreUpload } from '@/api/hooks/uploads';
import { useUploadErrorToast } from '@/api/error-toast';
import { ClientFileSchema } from '@/api/schemas';
import { useToast } from '@/hooks/use-toast';
import type { ListUploadsResponse } from '@/api/types';
import { pickFiles, pickImages, readPicked, takePhoto, type PickedRef } from '@/lib/platform';
import { createUploadBatch, deleteUploadBatch, postBatchFile } from '@/api/hooks/uploads';
import { screenLocally, sendBatch } from '@/lib/batchUpload';
import { rejectionSummary, saveRejected, type RejectedFile } from '@/lib/batchOutcomes';
import { plural } from '@/components/uploads/PendingReviewBar';
import { prepareForUpload } from '@/lib/image-prep';
import { selectRecoverableUpload, type PickedFileMeta } from '@/lib/upload-recovery';
import {
  hasSeenUploadDisclaimer,
  markUploadDisclaimerSeen,
} from '@/lib/disclaimer-storage';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { UploadApiError } from '@/api/types';

type Source = 'file' | 'camera' | 'gallery';

// HEAL-46a D-11 — потолок пакета. Держится здесь же, где и подпись о нём:
// значение и текст расходиться не должны.
const MAX_BATCH_FILES = 20;

// Те же границы, что и на сервере. Расходиться им нельзя: иначе клиент пропустит
// файл, который сервер отобьёт, и человек увидит отказ вместо понятной подписи.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif'];

// HEAL-48: этапы названы так же, как их видит пользователь дальше на экране
// обработки — «Анализируем документ» уже живёт там, здесь его не дублируем.
const STAGE_LABEL = {
  preparing: 'Подготавливаем файл',
  uploading: 'Загружаем анализ',
  sending: 'Отправляем файлы',
} as const;

type Props = {
  onClose: () => void;
  onUploaded: (uploadId: number) => void;
  /**
   * HEAL-46a — куда уйти, когда выбран не один файл, а несколько.
   *
   * Необязательный: места, где пакет не имеет смысла, просто его не передают, и
   * там выбор по-прежнему ограничен одним файлом.
   */
  onBatchCreated?: (batchId: number) => void;
};

export function UploadSourcePicker({ onClose, onUploaded, onBatchCreated }: Props) {
  const { toast } = useToast();
  const showUploadError = useUploadErrorToast();
  const storeUpload = useStoreUpload();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // HEAL-48: раньше здесь был голый `busy`, и единственным следом работы была
  // погасшая на 60% кнопка. Между выбором файла и переходом на экран обработки
  // проходит 2–5 секунд (сжатие фото на устройстве + POST), и всё это время
  // экран выглядел мёртвым — люди считали приложение зависшим и жали ещё раз.
  // Теперь стадия названа вслух. `null` = ничего не делаем; во время открытого
  // системного пикера стадии тоже нет — там пользователь уже видит системный UI,
  // и оверлей под ним был бы враньём.
  const [stage, setStage] = useState<null | 'preparing' | 'uploading' | 'sending'>(null);
  const [sentCount, setSentCount] = useState({ sent: 0, total: 0 });
  // Отмена читается синхронно между файлами, поэтому ref, а не состояние:
  // состояние обновится к следующему рендеру, а решение нужно прямо сейчас.
  const cancelRef = useRef(false);
  const [cancelling, setCancelling] = useState(false);
  // Насколько отправка долгая — от этого зависит формулировка, но НЕ сам факт
  // предупреждения: закрытие приложения обрывает передачу всегда.
  const [longSending, setLongSending] = useState(false);

  // Сколько файлов разрешено выбрать за раз. Без обработчика пакета — один, как
  // было до HEAL-46a.
  const maxFiles = onBatchCreated ? MAX_BATCH_FILES : 1;
  const [pickerOpen, setPickerOpen] = useState(false);
  const busy = pickerOpen || stage !== null;

  useEffect(() => {
    if (!hasSeenUploadDisclaimer()) setDisclaimerOpen(true);
  }, []);

  const acceptDisclaimer = () => {
    markUploadDisclaimerSeen();
    setDisclaimerOpen(false);
  };

  const handleSource = async (source: Source) => {
    if (disclaimerOpen || busy) return;
    setPickerOpen(true);
    try {
      // Камера остаётся по одному кадру: снять пять фотографий подряд — это не
      // «выбрать пять», а пять отдельных действий (D-6).
      let refs: PickedRef[];
      try {
        if (source === 'camera') {
          const shot = await takePhoto();
          refs = shot ? [{ name: shot.name, mimeType: shot.mimeType, size: shot.blob.size, blob: shot.blob }] : [];
        } else {
          refs = source === 'file' ? await pickFiles(maxFiles) : await pickImages(maxFiles);
        }
      } finally {
        setPickerOpen(false);
      }
      if (refs.length === 0) return;

      // Несколько файлов — пакетный путь. Одиночный оставлен нетронутым: это
      // самый частый сценарий, и ломать его ради пакета незачем (I-2).
      if (refs.length > 1 && onBatchCreated) {
        await handleBatch(refs);

        return;
      }

      const picked = await readPicked(refs[0]);

      // HEAL-48: индикатор включаем ЗДЕСЬ — сразу как файл выбран, не дожидаясь
      // ни локальной подготовки, ни ответа сервера (это и есть требование тикета).
      setStage('preparing');
      const prepared = await prepareForUpload(picked);
      if ('kind' in prepared) {
        toast({
          title: 'Не удалось обработать фото',
          description: 'Попробуйте JPEG или PNG.',
          variant: 'destructive',
        });
        return;
      }

      const file = new File([prepared.blob], prepared.name, { type: prepared.mimeType });
      const validation = ClientFileSchema.safeParse({ size: file.size, type: file.type });
      if (!validation.success) {
        toast({
          title: 'Файл не подходит',
          description: validation.error.issues[0]?.message ?? 'Проверьте файл.',
          variant: 'destructive',
        });
        return;
      }

      const startedAt = Date.now();
      setStage('uploading');
      try {
        const response = await storeUpload.mutateAsync({ file });
        onUploaded(response.upload_id);
      } catch (e) {
        const err = e as AxiosError<UploadApiError>;
        // A2: таймаут / обрыв без ответа сервера (медленная запись на диск VPS под
        // нагрузкой). Загрузка могла успешно создаться на бэке — НЕ показываем
        // хард-ошибку: ищем именно нашу свежую in-flight загрузку и уводим в поток.
        if (axios.isAxiosError(err) && !err.response) {
          const resumed = await tryResumeInFlightUpload(
            { name: file.name, size: file.size, type: file.type },
            startedAt,
          );
          if (resumed) return;
          toast({
            title: 'Загрузка идёт дольше обычного',
            description: 'Проверьте Главную через минуту — анализ может уже обрабатываться.',
            variant: 'warning',
            dedupeKey: 'upload:slow',
          });
          return;
        }
        showUploadError(err);
      }
    } catch (e) {
      // Ошибки выбора/подготовки файла (pickFile/prepareForUpload) — обычный тост.
      showUploadError(e as AxiosError<UploadApiError>);
    } finally {
      setStage(null);
    }
  };

  /**
   * Пакетная отправка.
   *
   * Подготовка (сжатие, конверсия HEIC) живёт внутри отправки каждого файла, а не
   * заранее для всего набора: иначе в памяти оказался бы весь пакет — ровно то,
   * от чего уходили (D-3).
   */
  const handleBatch = async (refs: PickedRef[]) => {
    // Потолок пакета держим ЗДЕСЬ, а не в пикере (D-11).
    //
    // Системный выбор про наш потолок не знает: он умеет либо «один файл», либо
    // «сколько угодно», и если попросить у него число — молча отдаст один
    // (см. комментарий в platform.ts). Поэтому берём всё и режем сами.
    //
    // Лишние не выбрасываются молча: они поедут в список пакета с подписью
    // «Не поместился» (D-25 — человек видит все файлы, которые выбрал).
    const overflow = refs.slice(maxFiles).map((ref, i) => ({
      position: maxFiles + i,
      name: ref.name,
      reason: 'over_limit' as const,
    }));

    if (overflow.length > 0) {
      toast({
        title: `Отправим первые ${maxFiles}`,
        description: `Выбрано ${refs.length} файлов. Остальные ${overflow.length} выберите следующим пакетом.`,
        variant: 'warning',
      });
    }

    // Размер и формат отсеиваем здесь: клиент знает и то, и другое из выбора, и
    // гнать заведомо негодный файл на сервер незачем (D-25).
    const { passed, rejected } = screenLocally(refs.slice(0, maxFiles), {
      maxBytes: MAX_FILE_BYTES,
      allowedTypes: ALLOWED_TYPES,
    });

    // Круг ревью 2, находка 2: после отсева могло остаться меньше двух файлов.
    // Пакет из одного сервер не создаёт (и правильно), но и терять этот файл
    // нельзя — отправляем его обычной одиночной загрузкой.
    if (passed.length === 0) {
      toast({
        title: 'Нечего загружать',
        description: rejected.length === 1
          ? `Выбранный файл не подошёл: ${rejectionSummary(rejected)}.`
          : `Ни один из ${rejected.length} файлов не подошёл: ${rejectionSummary(rejected)}.`,
        variant: 'destructive',
      });

      return;
    }

    if (passed.length === 1) {
      // ⚠️ Отсеянные объясняем ЗДЕСЬ, а не молчим.
      //
      // Экрана пакета в этой ветке не будет — файл уходит обычной одиночной
      // загрузкой, — и список исходов показать негде. Без этого человек
      // выбирал два файла, видел загрузку одного и не узнавал, куда делся
      // второй: ровно тот дефект, ради которого писался D-25 (находка ревью
      // 24.09).
      if (rejected.length > 0 || overflow.length > 0) {
        const lost = rejected.length + overflow.length;
        toast({
          title: `Отправляем 1 файл из ${lost + 1}`,
          description: `${plural(lost, 'Остальной', 'Остальные', 'Остальные')} не подошли: ${rejectionSummary([...rejected.map((r) => ({ reason: r.reason })), ...overflow])}.`,
          variant: 'warning',
        });
      }

      await sendSingle(passed[0].ref);

      return;
    }

    setSentCount({ sent: 0, total: passed.length });
    const totalBytes = passed.reduce((sum, item) => sum + item.ref.size, 0);
    setLongSending(passed.length > 3 || totalBytes > 20 * 1024 * 1024);
    cancelRef.current = false;
    setCancelling(false);
    setStage('sending');

    const result = await sendBatch(passed, {
      isCancelled: () => cancelRef.current,
      createBatch: createUploadBatch,
      sendOne: async (file, meta) => {
        const prepared = await prepareForUpload(file);
        if ('kind' in prepared) {
          throw { response: { data: { error: 'unsupported_file' } } };
        }

        return postBatchFile(prepared, meta);
      },
      onProgress: (sent, total) => setSentCount({ sent, total }),
    });

    if (result.batchId === null) return;

    // Исходы, которых сервер не знает: отклонённые до отправки и отклонённые им
    // самим (дубли). Без них человек видит «выбрал 5, загрузилось 4» и никакого
    // объяснения — именно это и было главной жалобой (D-25).
    const rejectedFiles: RejectedFile[] = [
      ...overflow,
      ...rejected.map((r) => ({ position: r.position, name: r.ref.name, reason: r.reason })),
      ...result.outcomes.flatMap<RejectedFile>((o) => {
        if (o.status === 'duplicate') {
          return [{
            position: o.position,
            name: o.ref.name,
            reason: 'duplicate',
            existingUploadId: o.existingUploadId,
          }];
        }
        if (o.status === 'not_sent') {
          return [{ position: o.position, name: o.ref.name, reason: 'not_sent' }];
        }
        // Круг ревью 2, находка 1: файл, упавший с ошибкой отправки, тоже обязан
        // остаться в списке. Иначе у него нет ни строки на сервере, ни записи
        // здесь — он исчезает бесследно, то есть ровно тот дефект, который всё
        // это и чинит.
        if (o.status === 'failed') {
          return [{ position: o.position, name: o.ref.name, reason: 'send_failed' }];
        }

        return [];
      }),
    ];

    // Отмена означает отмену: уже принятые файлы удаляются, иначе человек
    // думает, что не осталось ничего, а в истории появляется половина пакета
    // (D-23). Удаление идёт тем же механизмом, что и кнопка «Удалить пакет».
    if (cancelRef.current) {
      try {
        await deleteUploadBatch(result.batchId);
      } catch {
        // Не удалось — человек увидит пакет на своём экране и сможет удалить
        // вручную. Молча терять его нельзя.
      }
      toast({ title: 'Загрузка отменена', description: 'Отправленные файлы удалены.', variant: 'success' });

      return;
    }

    saveRejected(result.batchId, rejectedFiles);
    onBatchCreated?.(result.batchId);
  };

  /** Один файл — обычная загрузка, тот же путь, что был до пакетов. */
  const sendSingle = async (ref: PickedRef) => {
    setStage('preparing');
    const picked = await readPicked(ref);
    const prepared = await prepareForUpload(picked);
    if ('kind' in prepared) {
      toast({
        title: 'Не удалось обработать файл',
        description: 'Попробуйте JPEG, PNG или PDF.',
        variant: 'destructive',
      });

      return;
    }

    const file = new File([prepared.blob], prepared.name, { type: prepared.mimeType });
    setStage('uploading');
    const response = await storeUpload.mutateAsync({ file });
    onUploaded(response.upload_id);
  };

  // A2-recovery: после upload-таймаута перечитываем список и через identity-матч
  // (имя+размер+mime, см. selectRecoverableUpload) находим ИМЕННО нашу загрузку,
  // дошедшую до бэка несмотря на оборванный ответ. Уводим в Processing-поток.
  const tryResumeInFlightUpload = async (
    picked: PickedFileMeta,
    startedAtMs: number,
  ): Promise<boolean> => {
    try {
      const { data } = await api.get<ListUploadsResponse>('/uploads');
      const match = selectRecoverableUpload(data.data, picked, startedAtMs);
      if (match) {
        onUploaded(match.id);
        return true;
      }
    } catch {
      // best-effort recovery — при сбое просто покажем мягкий тост выше
    }
    return false;
  };

  return (
    <div className="relative p-6">
      {/* HEAL-48: перекрывает лист целиком, поэтому повторный тап по источнику
          физически невозможен — раньше от дубля спасал только disabled. */}
      {stage !== null && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/95"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary-text" aria-hidden="true" />
          <div className="text-sm font-semibold">
            {STAGE_LABEL[stage]}
            {stage === 'sending' && sentCount.total > 0 && ` · ${sentCount.sent} из ${sentCount.total}`}
          </div>
          {/* ⚠️ Предупреждение показывается ВСЕГДА, пока идёт отправка.
              Раньше оно появлялось только на пакете больше трёх файлов или
              тяжелее 20 МБ (D-16, TC-F-015): «пара небольших файлов уходит за
              секунды, там пугать нечем». На устройстве 24.09 это стоило двух
              потерянных файлов — человек закрыл приложение на второй секунде
              отправки, и обе передачи оборвались.

              Довод «уходит за секунды» неверен вдвойне. На мобильной сети
              секунд больше, чем кажется. А главное — 46b сам научил человека
              закрывать приложение: «можно закрыть, сообщим, когда будет
              готово». Это правда ПОСЛЕ отправки и ложь ВО ВРЕМЯ неё, и разницу
              обязаны объяснять мы, а не он. */}
          <div className="px-8 text-center text-xs text-muted-foreground">
            {stage === 'sending'
              ? longSending
                ? 'Не закрывайте приложение — файлы ещё отправляются, это займёт около минуты.'
                : 'Не закрывайте приложение — файлы ещё отправляются.'
              : 'Не закрывайте приложение — это займёт несколько секунд.'}
          </div>
          {stage === 'sending' && (
            <button
              type="button"
              onClick={() => {
                cancelRef.current = true;
                setCancelling(true);
              }}
              disabled={cancelling}
              className="mt-1 px-6 py-2 text-sm font-medium text-muted-foreground disabled:opacity-50"
            >
              {cancelling ? 'Отменяем…' : 'Отменить'}
            </button>
          )}
        </div>
      )}

      <h2 className="mb-1 text-xl font-bold">Откуда загрузить?</h2>
      <p className="mb-5 text-sm text-muted-foreground">
        PDF, JPG, PNG, HEIC. До 10 МБ.
        {maxFiles > 1 && ` Можно выбрать несколько — до ${maxFiles} за раз.`}
      </p>

      <div className="mb-5 space-y-3">
        <SourceButton
          icon={<FileText className="h-6 w-6" />}
          label="PDF из файлов"
          hint="Из почты, Drive, Files"
          highlighted
          disabled={busy}
          onClick={() => handleSource('file')}
        />
        <SourceButton
          icon={<Camera className="h-6 w-6" />}
          label="Сфотографировать"
          hint="Камера прямо сейчас"
          disabled={busy}
          onClick={() => handleSource('camera')}
        />
        <SourceButton
          icon={<ImageIcon className="h-6 w-6" />}
          label="Из галереи"
          hint="Сохранённое фото или скриншот"
          disabled={busy}
          onClick={() => handleSource('gallery')}
        />
      </div>

      <div className="rounded-surface border border-blue-200 bg-blue-50 p-3 text-hl-small leading-snug text-blue-900">
        <div className="mb-0.5 font-semibold">💡 Подсказка</div>
        PDF от лаборатории распознаётся точнее, чем фото бланка. Если есть оба — выбирайте PDF.
      </div>

      <button
        type="button"
        onClick={onClose}
        className="mt-4 w-full py-2 text-sm font-medium text-muted-foreground"
      >
        Отмена
      </button>

      <Dialog open={disclaimerOpen} onOpenChange={() => undefined}>
        <DialogContent
          className="max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Перед загрузкой</DialogTitle>
            <DialogDescription className="text-left">
              Загружая файл, вы понимаете, что AI может ошибиться — всегда проверяйте
              результат сами. После загрузки данные пойдут в сервис AI для распознавания.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={acceptDisclaimer}>Понятно</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SourceButton({
  icon,
  label,
  hint,
  highlighted,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  highlighted?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'flex w-full items-center gap-4 rounded-surface p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-60',
        highlighted
          ? 'border-2 border-primary/30 bg-primary/5'
          : 'border border-border bg-surface',
      ].join(' ')}
    >
      <div
        className={[
          'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-surface',
          highlighted ? 'bg-primary text-primary-foreground' : 'bg-muted text-gray-700',
        ].join(' ')}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <svg
        className="h-5 w-5 text-gray-400"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}
